import React, { useState, useCallback, useEffect } from "react";
import { useHttpClient } from "shared/hooks/http-hook";

const SOURCE_LABELS = {
  landing: "Landing (routebot.com)",
  app: "Uygulama (subscription)",
};

const TYPE_LABELS = {
  button_click: "Buton Tıklama",
  modal_view: "Modal Görüntüleme",
  page_view: "Sayfa Görüntüleme",
  other: "Diğer",
};

// Her event ismi için kısa, insan-okunur açıklama. Bir bakışta ne olduğunu anlatır.
const NAME_DESCRIPTIONS = {
  // App — Demo
  "Demo — Opened": "Demo uygulaması yüklendi (her yükleme/yeniden yükleme sayılır).",
  "Demo — Optimize Coach Shown": "Optimize Route butonunun yanındaki ipucu balonu gösterildi.",
  "Demo — Optimize Pressed": "Demo içinde Optimize Route butonuna basıldı.",
  "Demo — Book a Demo (persistent)": "Demo'daki sabit 'Book a demo' butonuna tıklandı.",
  "Demo — Book a Demo (navbar)": "Demo üst menüsündeki 'Book a Demo' butonuna tıklandı.",
  // App — Onboarding
  "Onboarding — Started": "Onboarding tanıtım akışı görüntülendi (ilk adım).",
  "Onboarding — Welcome Started": "Onboarding'de 'Let's go' ile karşılama adımı başlatıldı.",
  "Onboarding — Industry Selected": "Onboarding'de sektör seçildi.",
  "Onboarding — Personalized Demo Created": "Kişiselleştirilmiş demo hazırlanıyor adımına geçildi.",
  "Onboarding — Completed": "Onboarding tamamlandı, servisler sayfasına yönlendirildi.",
  "Onboarding — Skipped": "Onboarding atlandı.",
  // App — Login / Signup
  "Login — Try a Live Demo": "Login sayfasındaki 'Try a Live Demo' butonuna tıklandı.",
  "Login — Create Account": "Login sayfasından hesap oluşturmaya gidildi.",
  "Signup — Start Demo (Demo Card)": "Kayıt sayfasındaki demo kartından demo başlatıldı.",
  "Signup — Create Account (Submit)": "Kayıt formu gönderildi (hesap oluşturma).",
  "Signup — Go to Login": "Kayıt sayfasından login'e geçildi.",
  "Signup — Reset Password": "Şifre sıfırlama bağlantısına tıklandı.",
  // App — Subscription
  "Subscription — Page View": "Abonelik sayfası görüntülendi.",
  "Subscription — Manage Billing (Portal)": "Faturalandırma portalı (Stripe) açıldı.",
  "Subscription — Change/Upgrade Plan": "Plan değiştir/yükselt tıklandı.",
  "Subscription — Cancel clicked": "Abonelik iptali tıklandı.",
  "Subscription — Reactivate clicked": "Abonelik yeniden etkinleştirme tıklandı.",
  // Book a Demo (app + landing ortak)
  "Book a Demo — Opened": "Book a Demo modalı açıldı.",
  "Book a Demo — Submitted": "Book a Demo formu gönderildi (lead yakalandı).",
  "Book a Demo — Pick a Time (Calendly)": "Book a Demo'da Calendly ile zaman seçimi tıklandı.",
  // Landing — Hero
  "Hero — Try Live Demo": "Landing Hero bölümündeki 'Try Live Demo' tıklandı.",
  "Hero — Start Free": "Landing Hero'daki 'Start Free' (kayıt) tıklandı.",
  "Hero — Watch 90s Video": "Hero'daki '90 saniyelik video' butonuna tıklandı.",
  "Hero — Comparison Strip (open video)": "Hero karşılaştırma görseline tıklanarak video açıldı.",
  "Hero Image — Watch Demo": "Hero görselindeki oynat düğmesiyle video açıldı.",
  // Landing — Navbar
  "Navbar — Try Live Demo": "Üst menüdeki 'Try Live Demo' (masaüstü) tıklandı.",
  "Navbar (mobile) — Try Live Demo": "Üst menüdeki 'Try Live Demo' (mobil) tıklandı.",
  "Navbar — Login": "Üst menüdeki 'Login' (masaüstü) tıklandı.",
  "Navbar (mobile) — Login": "Üst menüdeki 'Login' (mobil) tıklandı.",
  "Navbar — Sign Up": "Üst menüdeki 'Sign Up' (masaüstü) tıklandı.",
  "Navbar (mobile) — Sign Up": "Üst menüdeki 'Sign Up' (mobil) tıklandı.",
  // Landing — diğer bölümler
  "Trusted Section — Try Live Demo": "'Built for Real Operations' bölümündeki demo butonu tıklandı.",
  "Benefits — See How It Works": "Benefits bölümündeki 'See How It Works' tıklandı.",
  "Demo Section — Watch Walkthrough": "Video bölümündeki 'Watch a Real Walkthrough' tıklandı.",
  // Landing — Demo/Video modalları
  "Demo Modal — Viewed": "Landing'de otomatik açılan demo davet modalı görüntülendi.",
  "Demo Modal — See Live Demo": "Demo davet modalındaki 'See live demo' tıklandı.",
  "Demo Modal — Book a Call": "Demo davet modalındaki 'Book a call' tıklandı.",
  "Video Modal — Viewed": "Tanıtım videosu modalı açıldı (kaynak meta'da).",
};

// Bilinmeyen isimler için prefix'e göre genel açıklama üret.
const describeByPrefix = (name) => {
  if (name.startsWith("Onboarding —")) return "Demo onboarding akışı olayı.";
  if (name.startsWith("Subscription —")) return "Abonelik sayfası olayı.";
  if (name.startsWith("Book a Demo") || name.startsWith("Demo Modal")) return "Demo talebi / lead olayı.";
  if (name.startsWith("Hero")) return "Landing Hero bölümü olayı.";
  if (name.startsWith("Navbar")) return "Landing üst menü olayı.";
  if (name.startsWith("Video")) return "Video görüntüleme olayı.";
  if (name.startsWith("Login") || name.startsWith("Signup")) return "Giriş / kayıt sayfası olayı.";
  if (name.startsWith("Demo —") || name.startsWith("Demo Section")) return "Demo uygulaması olayı.";
  return "";
};

const getDescription = (name) => (name && NAME_DESCRIPTIONS[name]) || describeByPrefix(name || "");

const MOBILE_UA_RE = /Mobi|Android|iPhone|iPad|iPod|Windows Phone|BlackBerry|Opera Mini|IEMobile/i;
const deviceLabel = (userAgent) => {
  if (!userAgent) return "—";
  return MOBILE_UA_RE.test(userAgent) ? "Mobil" : "Masaüstü";
};

const formatDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString();
};

const SuiteEvents = () => {
  const { sendRequest } = useHttpClient();
  const [events, setEvents] = useState([]);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ source: "", type: "", name: "", from: "", to: "" });

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filters.source) params.set("source", filters.source);
      if (filters.type) params.set("type", filters.type);
      if (filters.name) params.set("name", filters.name);
      if (filters.from) params.set("from", filters.from);
      if (filters.to) params.set("to", filters.to);
      params.set("limit", "1000");

      const data = await sendRequest(
        `${process.env.REACT_APP_BACKEND_URL}/suite-events?${params.toString()}`,
      );
      setEvents(data.events || []);
      setSummary(data.summary || []);
    } catch (err) {
      setError(err?.message || "Eventler alınamadı.");
    }
    setLoading(false);
  }, [sendRequest, filters]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const setFilter = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));

  const resetFilters = () => setFilters({ source: "", type: "", name: "", from: "", to: "" });

  const inputStyle = {
    padding: "8px 10px",
    borderRadius: "6px",
    border: "1px solid #ddd",
    fontSize: "13px",
  };

  const thStyle = {
    textAlign: "left",
    padding: "8px 10px",
    borderBottom: "2px solid #e0e0e0",
    fontSize: "12px",
    color: "#666",
    whiteSpace: "nowrap",
  };

  const tdStyle = {
    padding: "8px 10px",
    borderBottom: "1px solid #f0f0f0",
    fontSize: "13px",
    verticalAlign: "top",
  };

  const sourceBadge = (source) => (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: "12px",
        fontSize: "11px",
        fontWeight: 600,
        background: source === "landing" ? "#e3f2fd" : "#ede7f6",
        color: source === "landing" ? "#1565c0" : "#5e35b1",
      }}
    >
      {source === "landing" ? "Landing" : "App"}
    </span>
  );

  return (
    <div style={{ padding: "20px", maxWidth: "1400px", overflowY: "auto" }}>
      <h2 style={{ marginTop: 0, marginBottom: "5px" }}>Suite Events</h2>
      <p style={{ color: "#666", fontSize: "13px", marginTop: 0, marginBottom: "20px" }}>
        Funnel olayları. <strong>Landing</strong>: routebot.com üzerindeki buton tıklamaları ve modal görüntülemeleri.{" "}
        <strong>App</strong>: uygulama içi funnel — demo, onboarding, login/signup ve abonelik olayları. Sadece okuma yapar.
      </p>

      {/* Filtreler */}
      <div
        style={{
          display: "flex",
          gap: "10px",
          flexWrap: "wrap",
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        <select style={inputStyle} value={filters.source} onChange={(e) => setFilter("source", e.target.value)}>
          <option value="">Tüm Kaynaklar</option>
          <option value="landing">{SOURCE_LABELS.landing}</option>
          <option value="app">{SOURCE_LABELS.app}</option>
        </select>

        <select style={inputStyle} value={filters.type} onChange={(e) => setFilter("type", e.target.value)}>
          <option value="">Tüm Tipler</option>
          <option value="button_click">{TYPE_LABELS.button_click}</option>
          <option value="modal_view">{TYPE_LABELS.modal_view}</option>
          <option value="page_view">{TYPE_LABELS.page_view}</option>
        </select>

        <input
          style={{ ...inputStyle, minWidth: "220px" }}
          type="text"
          placeholder="İsme göre ara (örn: Try Live Demo)"
          value={filters.name}
          onChange={(e) => setFilter("name", e.target.value)}
        />

        <label style={{ fontSize: "12px", color: "#666" }}>
          Başlangıç{" "}
          <input style={inputStyle} type="date" value={filters.from} onChange={(e) => setFilter("from", e.target.value)} />
        </label>
        <label style={{ fontSize: "12px", color: "#666" }}>
          Bitiş{" "}
          <input style={inputStyle} type="date" value={filters.to} onChange={(e) => setFilter("to", e.target.value)} />
        </label>

        <button
          onClick={fetchEvents}
          style={{
            padding: "8px 16px",
            borderRadius: "6px",
            border: "1px solid #1976d2",
            background: "#1976d2",
            color: "#fff",
            cursor: "pointer",
            fontSize: "13px",
            fontWeight: 600,
          }}
        >
          Yenile
        </button>
        <button
          onClick={resetFilters}
          style={{
            padding: "8px 16px",
            borderRadius: "6px",
            border: "1px solid #ddd",
            background: "#fff",
            color: "#444",
            cursor: "pointer",
            fontSize: "13px",
          }}
        >
          Temizle
        </button>
      </div>

      {error && <p style={{ color: "#c62828" }}>{error}</p>}
      {loading && <p style={{ color: "#666" }}>Yükleniyor…</p>}

      {/* Özet: isme göre sayımlar */}
      {!loading && summary.length > 0 && (
        <div style={{ marginBottom: "28px" }}>
          <h3 style={{ fontSize: "15px", marginBottom: "10px" }}>Özet (isme göre)</h3>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
              gap: "10px",
            }}
          >
            {summary.map((s, i) => (
              <div
                key={`${s.source}-${s.name}-${i}`}
                style={{
                  border: "1px solid #eee",
                  borderRadius: "8px",
                  padding: "10px 12px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ marginBottom: "4px" }}>{sourceBadge(s.source)}</div>
                  <div style={{ fontSize: "13px", fontWeight: 500, wordBreak: "break-word" }}>{s.name}</div>
                  {getDescription(s.name) && (
                    <div style={{ fontSize: "11px", color: "#999", marginTop: "3px", lineHeight: 1.35 }}>
                      {getDescription(s.name)}
                    </div>
                  )}
                </div>
                <div style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                  <div style={{ fontSize: "20px", fontWeight: 700, color: "#1976d2" }}>{s.count}</div>
                  {typeof s.mobile === "number" && (
                    <div style={{ fontSize: "10.5px", color: "#888", marginTop: "2px" }}>
                      Mobil {s.mobile} · Masaüstü {s.desktop}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Event tablosu */}
      {!loading && (
        <>
          <h3 style={{ fontSize: "15px", marginBottom: "10px" }}>
            Son Eventler {events.length > 0 ? `(${events.length})` : ""}
          </h3>
          {events.length === 0 ? (
            <p style={{ color: "#999" }}>Bu filtrelerle event bulunamadı.</p>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Tarih</th>
                    <th style={thStyle}>Kaynak</th>
                    <th style={thStyle}>Tip</th>
                    <th style={thStyle}>İsim</th>
                    <th style={thStyle}>Cihaz</th>
                    <th style={thStyle}>Sayfa</th>
                    <th style={thStyle}>UTM</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((ev) => (
                    <tr key={ev._id}>
                      <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>{formatDate(ev.createdAt)}</td>
                      <td style={tdStyle}>{sourceBadge(ev.source)}</td>
                      <td style={tdStyle}>{TYPE_LABELS[ev.type] || ev.type}</td>
                      <td style={{ ...tdStyle, fontWeight: 500 }}>{ev.name}</td>
                      <td style={{ ...tdStyle, color: "#666", whiteSpace: "nowrap" }}>{deviceLabel(ev.userAgent)}</td>
                      <td style={{ ...tdStyle, color: "#666", maxWidth: "220px", wordBreak: "break-word" }}>
                        {ev.path || ev.pageUrl || "—"}
                      </td>
                      <td style={{ ...tdStyle, color: "#666", fontSize: "12px" }}>
                        {ev.utm
                          ? [ev.utm.utm_source, ev.utm.utm_campaign].filter(Boolean).join(" / ") || "—"
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SuiteEvents;
